import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faExchangeAlt, faCopy, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { recordService, exchangeRateService } from '../../services/api';
import { Category, ExchangeRate, RecordType, AccountType } from '../../types';

type ImportMode = 'income' | 'expense';
type DuplicateHandling = 'skip' | 'override';
type NotifType = 'success' | 'warning' | 'error' | 'info';

interface Notification {
  message: string;
  type: NotifType;
}

export interface ImportPaymentsDialogProps {
  mode: ImportMode;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  categories: Category[];
  accountType: AccountType;
  currentRate: ExchangeRate | null;
}

const INCOME_DEFAULT_CATEGORY_ID = 11;
const EXPENSE_DEFAULT_CATEGORY_ID = 9;

const NOTIF_COLORS: Record<NotifType, string> = {
  success: '#d1e7dd',
  warning: '#fff3cd',
  error: '#f8d7da',
  info: '#cff4fc',
};

const ImportPaymentsDialog: React.FC<ImportPaymentsDialogProps> = ({
  mode,
  isOpen,
  onClose,
  onSuccess,
  categories,
  accountType,
  currentRate,
}) => {
  const isIncome = mode === 'income';
  const defaultCategoryId = isIncome ? INCOME_DEFAULT_CATEGORY_ID : EXPENSE_DEFAULT_CATEGORY_ID;

  const [rawData, setRawData] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsedRecords, setParsedRecords] = useState<any[]>([]);
  const [duplicateRecords, setDuplicateRecords] = useState<any[]>([]);
  const [duplicateHandling, setDuplicateHandling] = useState<DuplicateHandling>('skip');
  const [exchangeRate, setExchangeRate] = useState('');
  const [exchangeRateId, setExchangeRateId] = useState<number | null>(null);
  const [appliedExchangeRate, setAppliedExchangeRate] = useState<number | null>(null);
  const [showTextarea, setShowTextarea] = useState(true);
  const [notif, setNotif] = useState<Notification | null>(null);

  // Initialize exchange rate when dialog opens
  useEffect(() => {
    if (isOpen && currentRate) {
      const defaultRate = Math.round(currentRate.vnd_to_usd);
      setExchangeRate(defaultRate.toString());
      setExchangeRateId(currentRate.id);
      setAppliedExchangeRate(defaultRate);
    }
  }, [isOpen, currentRate]);

  const notify = (message: string, type: NotifType) => {
    setNotif({ message, type });
    if (type === 'success') setTimeout(() => setNotif(null), 5000);
  };

  const resetState = () => {
    setRawData('');
    setLoading(false);
    setParsedRecords([]);
    setDuplicateRecords([]);
    setDuplicateHandling('skip');
    setExchangeRate('');
    setExchangeRateId(null);
    setAppliedExchangeRate(null);
    setShowTextarea(true);
    setNotif(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ── Utilities ────────────────────────────────────────────────────────────

  const findOrCreateExchangeRate = async (rateValue: number): Promise<number> => {
    const response = await exchangeRateService.getHistory({
      limit: 1000,
      sortBy: 'effective_date',
      sortOrder: 'desc',
    });
    const rates = response.data.rates || [];
    const match = rates.find((r: any) => Math.round(r.vnd_to_usd) === rateValue);
    if (match) return match.id;
    const created = await exchangeRateService.create({
      effective_date: new Date().toISOString().split('T')[0],
      vnd_to_usd: rateValue,
    });
    return created.data.id;
  };

  const parseAmount = (str: string): number => {
    if (!str) return 0;
    const cleaned = str.replace(/[^\d,.]/g, '');
    if (!cleaned) return 0;
    if (cleaned.includes(',') && cleaned.includes('.')) {
      return cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
        ? parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0
        : parseFloat(cleaned.replace(/,/g, '')) || 0;
    }
    if (cleaned.includes(',')) {
      return cleaned.length - cleaned.lastIndexOf(',') <= 3
        ? parseFloat(cleaned.replace(',', '.')) || 0
        : parseFloat(cleaned.replace(/,/g, '')) || 0;
    }
    return parseFloat(cleaned) || 0;
  };

  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    if (dateStr.includes('/')) {
      const p = dateStr.split('/');
      if (p.length === 3) {
        const d = new Date(Date.UTC(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0])));
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  // ── Parsing ───────────────────────────────────────────────────────────────

  const buildParsedRecords = (data: string, rate: number, rateId: number | null): any[] => {
    const lines = data.trim().split('\n');
    const records: any[] = [];
    const dupes: any[] = [];
    const seen = new Map<string, boolean>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      let cols = line.split('\t');
      if (cols.length < 4 && line.match(/\d{2}\/\d{2}\/\d{4}/)) {
        cols = line.split(/\s{2,}/).filter(p => p.trim());
      }

      try {
        const dateStr = cols[0]?.trim();
        const timeStr = cols[1]?.trim();
        const description = cols[3]?.trim();
        if (!dateStr || !description) continue;

        const recordDate = parseDate(dateStr);
        if (!recordDate) continue;

        const formattedDate = recordDate.toISOString().split('T')[0];
        let recordTime = '00:00:00';
        if (timeStr?.includes(' ')) {
          const tp = timeStr.split(' ')[1];
          if (tp) recordTime = tp;
        }

        let record: any;

        if (isIncome) {
          const beforeTaxStr = cols[4]?.trim() || cols[cols.length - 1]?.trim();
          const afterTaxStr = cols[5]?.trim() || '';
          const taxAmountStr = cols[6]?.trim() || '';

          const beforeTax = parseAmount(beforeTaxStr);
          if (beforeTax <= 0) continue;

          let taxPercent = 10;
          let taxAmount: number;

          if (afterTaxStr && taxAmountStr) {
            taxAmount = parseAmount(taxAmountStr);
            if (taxAmount > 0 && beforeTax > 0) {
              taxPercent = (taxAmount / (beforeTax - taxAmount)) * 100;
            }
          } else {
            const dec = taxPercent / 100;
            taxAmount = Math.round(beforeTax - beforeTax / (1 + dec));
          }

          const amountUsd = beforeTax / rate;
          const taxUsd = taxAmount / rate;

          record = {
            record_date: formattedDate,
            record_time: recordTime,
            description,
            amount_vnd: beforeTax,
            amount_usd: parseFloat(amountUsd.toFixed(2)),
            tax_percent: parseFloat(taxPercent.toFixed(2)),
            tax_amount_vnd: -Math.abs(taxAmount),
            tax_amount_usd: -Math.abs(parseFloat(taxUsd.toFixed(2))),
            record_type: 'income' as RecordType,
            account_type: accountType,
            exchange_rate_id: rateId,
            category_id: defaultCategoryId,
            isDuplicate: false,
            excluded: false,
          };
        } else {
          const amountStr = cols[4]?.trim();
          const amount = parseAmount(amountStr);
          if (amount <= 0) continue;

          const amountUsd = amount / rate;

          record = {
            record_date: formattedDate,
            record_time: recordTime,
            description,
            amount_vnd: -Math.abs(amount),
            amount_usd: -Math.abs(parseFloat(amountUsd.toFixed(2))),
            tax_percent: 0,
            tax_amount_vnd: 0,
            tax_amount_usd: 0,
            record_type: 'expense' as RecordType,
            account_type: accountType,
            exchange_rate_id: rateId,
            category_id: defaultCategoryId,
            isDuplicate: false,
            excluded: false,
          };
        }

        const key = `${description.toLowerCase()}_${formattedDate}`;
        if (seen.has(key)) {
          record.isDuplicate = true;
          dupes.push(record);
        } else {
          seen.set(key, true);
        }
        records.push(record);
      } catch (err) {
        console.error(`Error parsing line ${i + 1}:`, line, err);
      }
    }

    setDuplicateRecords(dupes);
    return records;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleProcess = async () => {
    if (!rawData.trim()) { notify('Please paste data to import', 'warning'); return; }
    const rateValue = parseFloat(exchangeRate);
    if (!rateValue || rateValue <= 0) { notify('Please enter a valid exchange rate', 'warning'); return; }

    setLoading(true);
    setDuplicateRecords([]);
    try {
      const parsed = buildParsedRecords(rawData, rateValue, exchangeRateId);
      if (parsed.length === 0) { notify('No valid records found in the data', 'warning'); return; }

      const dupResponse = await recordService.checkDuplicates(parsed);
      const checked = dupResponse.data.results;

      const updated = parsed.map((r, i) =>
        i < checked.length && checked[i].isDuplicate
          ? { ...r, isDuplicate: true, duplicateId: checked[i].duplicateId }
          : r
      );

      const serverDups = updated.filter(r => r.isDuplicate);
      setDuplicateRecords(serverDups);
      setParsedRecords(updated);
      setShowTextarea(false); // Auto-hide after successful parse

      const dupCount = serverDups.length;
      notify(
        `Parsed ${updated.length} records (${updated.length - dupCount} unique, ${dupCount} duplicates)`,
        dupCount > 0 ? 'warning' : 'success'
      );
    } catch (err: any) {
      console.error('Error processing import:', err);
      notify(err.response?.data?.message || 'Error processing data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyExchangeRate = async () => {
    const rateValue = parseFloat(exchangeRate);
    if (!rateValue || rateValue <= 0) { notify('Please enter a valid exchange rate', 'warning'); return; }
    if (parsedRecords.length === 0) { notify('No records to apply exchange rate to', 'warning'); return; }

    setLoading(true);
    try {
      const newRateId = await findOrCreateExchangeRate(rateValue);
      setExchangeRateId(newRateId);

      const updated = parsedRecords.map(r => {
        const absVnd = Math.abs(r.amount_vnd);
        const newUsd = absVnd / rateValue;
        const amountUsd = isIncome
          ? parseFloat(newUsd.toFixed(2))
          : -Math.abs(parseFloat(newUsd.toFixed(2)));

        const absTaxVnd = Math.abs(r.tax_amount_vnd || 0);
        const taxUsd = isIncome && absTaxVnd > 0
          ? -Math.abs(parseFloat((absTaxVnd / rateValue).toFixed(2)))
          : 0;

        return { ...r, amount_usd: amountUsd, tax_amount_usd: taxUsd, exchange_rate_id: newRateId };
      });

      setParsedRecords(updated);
      setAppliedExchangeRate(rateValue);
      notify('Exchange rate applied successfully', 'success');
    } catch (err) {
      console.error('Error applying exchange rate:', err);
      notify('Error applying exchange rate', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index: number, field: string, value: any) => {
    const updated = [...parsedRecords];
    updated[index] = { ...updated[index], [field]: value };
    setParsedRecords(updated);
  };

  const handleToggleAll = (exclude: boolean) => {
    setParsedRecords(parsedRecords.map(r => ({ ...r, excluded: exclude })));
  };

  const handleConfirm = async () => {
    if (parsedRecords.length === 0) { notify('No data to import', 'warning'); return; }

    const toImport = parsedRecords.filter(r => !r.excluded);
    if (toImport.length === 0) { notify('All records are excluded. Nothing to import.', 'warning'); return; }

    const noCategory = toImport.filter(r => !r.category_id);
    if (noCategory.length > 0) {
      notify(`Please select a category for all ${noCategory.length} record(s)`, 'warning');
      return;
    }

    setLoading(true);
    try {
      let finalRateId = exchangeRateId;
      if (!finalRateId) {
        const rateValue = parseFloat(exchangeRate);
        finalRateId = await findOrCreateExchangeRate(rateValue);
        setExchangeRateId(finalRateId);
      }

      let successCount = 0, errorCount = 0, skippedCount = 0, overriddenCount = 0;
      const excludedCount = parsedRecords.length - toImport.length;

      for (const record of toImport) {
        if (record.isDuplicate) {
          if (duplicateHandling === 'skip') { skippedCount++; continue; }
          try { await recordService.delete(record.duplicateId); overriddenCount++; }
          catch (e) { console.error('Error deleting duplicate:', e); }
        }
        try {
          await recordService.create({ ...record, exchange_rate_id: finalRateId });
          successCount++;
        } catch (e) {
          console.error('Error creating record:', e);
          errorCount++;
        }
      }

      if (successCount > 0) {
        const parts = [`✅ Imported ${successCount} record${successCount !== 1 ? 's' : ''}`];
        if (excludedCount > 0) parts.push(`excluded ${excludedCount}`);
        if (skippedCount > 0) parts.push(`skipped ${skippedCount} dup${skippedCount !== 1 ? 's' : ''}`);
        if (overriddenCount > 0) parts.push(`overrode ${overriddenCount} dup${overriddenCount !== 1 ? 's' : ''}`);
        if (errorCount > 0) parts.push(`failed ${errorCount}`);
        notify(parts.join(', '), errorCount > 0 ? 'warning' : 'success');
        onSuccess();
        setTimeout(() => handleClose(), 1200);
      } else {
        notify('No records were imported', 'error');
      }
    } catch (err: any) {
      console.error('Error importing records:', err);
      notify(err.response?.data?.message || 'Error importing records', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const filteredCategories = categories.filter(
    cat => cat.type === (isIncome ? 'income' : 'expense') && cat.account_type === accountType
  );
  const displayRate = appliedExchangeRate || currentRate?.vnd_to_usd || 25000;
  const nonExcluded = parsedRecords.filter(r => !r.excluded);
  const nonExcludedDups = duplicateRecords.filter(r => !r.excluded);
  const importCount = duplicateHandling === 'override'
    ? nonExcluded.length
    : nonExcluded.length - nonExcludedDups.length;

  const allExcluded = parsedRecords.length > 0 && parsedRecords.every(r => r.excluded);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="modal-overlay" style={{ paddingLeft: '250px' }}>
      <div
        className="modal-container"
        style={{ maxWidth: 'calc(100vw - 300px)', width: '90%', minWidth: '900px', margin: '0 auto' }}
      >
        <div className="modal-header">
          <h3>Import {isIncome ? 'Income' : 'Expense'} Payments</h3>
          <button className="btn btn-sm btn-light modal-close-btn" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {/* Inline notification */}
          {notif && (
            <div
              style={{
                padding: '8px 12px',
                marginBottom: '10px',
                borderRadius: '4px',
                backgroundColor: NOTIF_COLORS[notif.type],
                fontSize: '0.85rem',
              }}
            >
              {notif.message}
            </div>
          )}

          {/* Raw data textarea */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0" style={{ fontSize: '0.85rem' }}>
                Paste Excel Data (Tab-delimited):
              </label>
              {parsedRecords.length > 0 && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowTextarea(!showTextarea)}
                >
                  <FontAwesomeIcon icon={showTextarea ? faChevronUp : faChevronDown} className="me-1" />
                  {showTextarea ? 'Hide' : 'Show'} Input
                </button>
              )}
            </div>
            {showTextarea && (
              <>
                <p className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                  {isIncome
                    ? 'Columns: Date · DateTime · Record ID · Description · Before Tax · After Tax (opt) · Tax Amount (opt)'
                    : 'Columns: Date · DateTime · Record ID · Description · Amount (5th column)'}
                  <br />
                  <strong>Note:</strong> After parsing, you can edit description and select category inline.
                </p>
                <textarea
                  className="form-control"
                  rows={8}
                  value={rawData}
                  onChange={e => setRawData(e.target.value)}
                  placeholder="Paste your Excel data here..."
                  disabled={loading}
                  style={{ fontSize: '0.82rem' }}
                />
              </>
            )}
          </div>

          {/* Controls: exchange rate + duplicate handling */}
          {parsedRecords.length > 0 && (
            <>
              <div className="mb-3 p-2 bg-light border rounded">
                <div className="d-flex align-items-center gap-4 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold text-nowrap" style={{ fontSize: '0.82rem' }}>
                      <FontAwesomeIcon icon={faExchangeAlt} className="me-1" />
                      Rate (VND/USD):
                    </span>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: '110px' }}
                      value={exchangeRate}
                      onChange={e => setExchangeRate(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleApplyExchangeRate}
                      disabled={loading || !exchangeRate}
                    >
                      Apply
                    </button>
                  </div>

                  {duplicateRecords.length > 0 && (
                    <>
                      <div className="vr" style={{ height: '28px' }} />
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-nowrap" style={{ fontSize: '0.82rem' }}>
                          <FontAwesomeIcon icon={faCopy} className="me-1" />
                          Duplicates ({nonExcludedDups.length}):
                        </span>
                        {(['skip', 'override'] as DuplicateHandling[]).map(opt => (
                          <div key={opt} className="form-check form-check-inline mb-0">
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`dupHandling_${mode}`}
                              id={`dup_${mode}_${opt}`}
                              value={opt}
                              checked={duplicateHandling === opt}
                              onChange={() => setDuplicateHandling(opt)}
                              disabled={loading}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`dup_${mode}_${opt}`}
                              style={{ fontSize: '0.82rem' }}
                            >
                              {opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Preview table */}
              <div className="mb-2">
                <h6 style={{ fontSize: '0.88rem', marginBottom: '6px' }}>
                  Preview ({nonExcluded.length} of {parsedRecords.length} records) — Edit inline:
                </h6>
                <div style={{ maxHeight: '430px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <table
                    className="table table-sm table-striped mb-0"
                    style={{ tableLayout: 'fixed', width: '100%', fontSize: '0.8rem' }}
                  >
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                      <tr>
                        <th style={{ width: '32px', textAlign: 'center', padding: '4px 3px' }}>
                          <input
                            type="checkbox"
                            title="Exclude all"
                            checked={allExcluded}
                            onChange={e => handleToggleAll(e.target.checked)}
                          />
                        </th>
                        <th style={{ width: '86px', padding: '4px 3px' }}>Date</th>
                        <th style={{ padding: '4px 3px' }}>Description</th>
                        <th style={{ width: '132px', padding: '4px 3px' }}>Category</th>
                        <th style={{ width: '98px', textAlign: 'right', padding: '4px 3px' }}>Amount VND</th>
                        <th style={{ width: '68px', textAlign: 'right', padding: '4px 3px' }}>USD</th>
                        {isIncome && (
                          <th style={{ width: '50px', textAlign: 'right', padding: '4px 3px' }}>Tax%</th>
                        )}
                        <th style={{ width: '74px', textAlign: 'right', padding: '4px 3px' }}>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRecords.map((record, idx) => (
                        <tr
                          key={idx}
                          className={
                            record.excluded
                              ? 'table-secondary'
                              : record.isDuplicate
                              ? 'table-warning'
                              : ''
                          }
                          style={{ opacity: record.excluded ? 0.5 : 1 }}
                        >
                          <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '3px' }}>
                            <input
                              type="checkbox"
                              checked={record.excluded || false}
                              onChange={e => handleEdit(idx, 'excluded', e.target.checked)}
                              title="Exclude from import"
                            />
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '3px', whiteSpace: 'nowrap' }}>
                            {record.record_date}
                          </td>
                          <td style={{ padding: '3px' }}>
                            <textarea
                              className="form-control form-control-sm"
                              value={record.description}
                              onChange={e => handleEdit(idx, 'description', e.target.value)}
                              rows={2}
                              style={{
                                fontSize: '0.8rem',
                                lineHeight: '1.25',
                                resize: 'vertical',
                                minHeight: '38px',
                                width: '100%',
                              }}
                            />
                            {record.isDuplicate && (
                              <span
                                className="badge bg-warning text-dark"
                                style={{ fontSize: '0.68rem' }}
                                title="Duplicate detected"
                              >
                                Dup
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '3px' }}>
                            <select
                              className="form-select form-select-sm"
                              value={record.category_id || ''}
                              onChange={e => handleEdit(idx, 'category_id', parseInt(e.target.value))}
                              style={{
                                fontSize: '0.78rem',
                                borderColor: !record.category_id ? '#dc3545' : undefined,
                              }}
                            >
                              <option value="">Select...</option>
                              {filteredCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '3px' }}>
                            {Math.abs(record.amount_vnd).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '3px' }}>
                            {Math.abs(record.amount_usd).toFixed(1)}
                          </td>
                          {isIncome && (
                            <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '3px' }}>
                              {record.tax_percent}%
                            </td>
                          )}
                          <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '3px' }}>
                            {Math.round(displayRate).toLocaleString('en-US')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          {parsedRecords.length === 0 ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleProcess}
              disabled={loading || !rawData.trim()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSync} spin className="me-1" />
                  Processing...
                </>
              ) : (
                'Parse Data'
              )}
            </button>
          ) : (
            <div className="d-flex gap-2 align-items-center">
              {nonExcludedDups.length > 0 && (
                <span
                  className={`small ${duplicateHandling === 'override' ? 'text-danger' : 'text-warning'}`}
                >
                  <strong>{nonExcludedDups.length}</strong> dup
                  {nonExcludedDups.length !== 1 ? 's' : ''} will be{' '}
                  {duplicateHandling === 'override' ? 'overridden' : 'skipped'}
                </span>
              )}
              <button
                className="btn btn-success btn-sm"
                onClick={handleConfirm}
                disabled={loading || importCount <= 0}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSync} spin className="me-1" />
                    Importing...
                  </>
                ) : (
                  `Import ${importCount} Record${importCount !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportPaymentsDialog;
